<template>
  <ContentTemplate title="Invite Team" :container-size-small="false">
    <form autocomplete="off">
      <div class="container">
        <div class="row" v-for="(item, index) in emailList" :key="index">
          <div class="col-8">
            <div class="teammate-group">
              <InputElement v-model="item.email" :name="'email_' + index" type="email"
                            placeholder="name@company.com"/>
              <DescriptionDropdownElement v-model:value="item.workspaceRole"
                                          name="workspaceRole" :options="teamOptions"
                                          class="btn-auth" title="Roles"/>
            </div>
            <div v-if="v$.$dirty && v$.emailList.$each.$response.$errors[index].email.length"
                 class="validation-error">
              <LucideIcon icon="Info"/>
              <span
                  v-for="(error, index) of v$.emailList.$each.$response.$errors[index].email"
                  :key="index"
              >
                {{ error.$message }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="hasProProduct" class="row">
         <div class="col-8 message">
           <span class="tag--default tag--orange">A new license for this member will be charged immediately</span>
         </div>
        </div>

        <div class="row action">
          <div class="col-8 btn-groups">
            <ButtonElement class="btn--ms btn--neutral btn--regular" @clickOnButton="addInputToForm()">
              <LucideIcon icon="Plus"/>
              Add Member
            </ButtonElement>
            <ButtonElement class="btn--ms btn--accent"
                           @clickOnButton="sendInvitation()">
              Send Invitation
            </ButtonElement>
          </div>
        </div>

      </div>
    </form>
  </ContentTemplate>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {type DropdownNs, SettingNs, WorkspaceNs} from '@shared/types'
import useVuelidate from '@vuelidate/core'

import {email, helpers} from '@vuelidate/validators'
import {mapStores} from "pinia";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useGeneralStore} from "@shared/stores/general.store";
import {ButtonElement, ContentTemplate, DescriptionDropdownElement, InputElement} from "@shared/components";
import {useWsMemberStore} from "@domains/settings/stores/workspace-member.store";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'ProfileView',
  components: {
    ButtonElement,
    ContentTemplate,
    DescriptionDropdownElement,
    InputElement
  },
  data() {
    return {
      teamOptions: [
        {
          iconType: 'fal',
          icon: 'UserCog',
          name: 'Admin',
          value: WorkspaceNs.WorkspaceRoleEnum.ADMIN,
          content: 'Can manage workspace, control user access, and update billing details.'
        },
        {
          iconType: 'fal',
          icon: 'Users',
          name: 'Members',
          value: WorkspaceNs.WorkspaceRoleEnum.MEMBER,
          content: 'Can access the projects as manager, editor or viewer depending on permission.'
        }
      ] as DropdownNs.DropdownOptionsItem[],
      emailList: [
        {
          email: '' as string,
          workspaceRole: WorkspaceNs.WorkspaceRoleEnum.MEMBER as string
        },
        {
          email: '' as string,
          workspaceRole: WorkspaceNs.WorkspaceRoleEnum.MEMBER as string
        },
        {
          email: '' as string,
          workspaceRole: WorkspaceNs.WorkspaceRoleEnum.MEMBER as string
        }
      ] as WorkspaceNs.InviteMember[]
    }
  },
  setup() {
    return {v$: useVuelidate()}
  },
  validations() {
    return {
      emailList: {
        $each: helpers.forEach({
          email: {
            email
          }
        })
      }
    }
  },
  computed: {
    ...mapStores(useWorkspaceStore, useWsMemberStore, useGeneralStore),
    hasProProduct() {
      return this.workspaceStore.hasProProduct
    }
  },

  methods: {
    addInputToForm() {
      const newFormItem = {
        email: '' as string,
        workspaceRole: WorkspaceNs.WorkspaceRoleEnum.MEMBER as string
      } as WorkspaceNs.InviteMember

      this.emailList = [...this.emailList, newFormItem]
    },
    sendInvitation() {
      this.v$.$touch()
      // @ts-ignore
      if (this.v$.emailList.$invalid) {
        return
      }

      this.generalStore.setLoading(true)

      const payload = {
        emailAddresses: this.emailList.filter((item) => (item.email)),
        workspaceId: this.workspaceStore.getSelectedWorkspaceId
      } as WorkspaceNs.InviteMembersRequest;

      this.wsMemberStore.inviteMemberToWorkspace(payload).then(() => this.goToMembersPage())
          .catch(() => {
            this.generalStore.setLoading(false)
          })
    },

    goToMembersPage() {
      const workspaceId = this.workspaceStore.getSelectedWorkspaceId
      this.wsMemberStore.loadWorkspaceMembers(workspaceId).then(() => {
        this.generalStore.setLoading(false)
        toast.success('Invitation to your team member are sent');

        this.$router.push({name: SettingNs.SettingRouteEnums.MEMBERS})
      }).catch(() => {
        this.generalStore.setLoading(false)
      })
    }
  }
})
</script>

<style lang="scss" scoped>
.teammate-group {
  display: flex;
  gap: var(--space-16);
  margin-bottom: var(--space-16);
}

.action {
  margin-top: var(--space-16);
}

.message{
  display: flex;
  justify-content: center;
  .tag--default{
    width: 100%;
  }
}

.btn-groups{
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
}

.btn-auth {
  width: 250px;

  .btn {
    height: 45px !important;
  }
}
</style>
